// UsersList.jsx

import React, { useMemo, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Table, Container, Row, Col } from "react-bootstrap";
import TableFilter from "../../../components/TableFilter";
import { useQuery } from "@tanstack/react-query";
import CenterLoadingContainer from "../../../components/CenterLoadingContainer";

const fetchCompanyData = async (token) => {
  const url =
    MOBY_API_URL +
    "/api/usermanager/v1/users/" +
    token?.user?.sub +
    "/companies?txid=" + token.sessionId;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token.token,
    },
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

const fetchUsersList = async (token, companyId) => {
  const response = await fetch(
    MOBY_API_URL + `/api/devicemanager/v1/mobileusers?companyId=${companyId}&txid=${token.sessionId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token.token,
      },
    }
  );
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

const UsersList = ({ onUserSelect, setSelectedUser, token }) => {
  const columns = useMemo(
    () => [
      {
        accessorKey: "emailAddress",
        header: "Email",
      },
      {
        accessorKey: "firstName",
        header: "First Name",
      },
      {
        accessorKey: "lastName",
        header: "Last Name",
      },
    ],
    []
  );

  const {
    data: companyData,
    isLoading: fetchCompanyLoading,
    isError: fetchCompanyError,
  } = useQuery({
    queryKey: ["companyData"],
    queryFn: () => fetchCompanyData(token),
    enabled: !!token?.user?.sub,
  });

  const companyId = useMemo(() => {
    if (companyData?.data?.length > 0) {
      return companyData.data[0]?.companyId || null;
    }
    return null;
  }, [companyData]);

  const {
    data: usersList,
    isLoading: fetchListLoading,
    isError: fetchListError,
    isFetching,
  } = useQuery({
    queryKey: ["usersList", companyId],
    queryFn: () => fetchUsersList(token, companyId),
    enabled: !!companyId,
  });

  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    data: usersList?.data || [],
    columns,
    state: {
      globalFilter,
      rowSelection,
    },
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "auto",
    enableRowSelection: true,
    enableMultiRowSelection: false,
    getRowId: (row) => row.id,
  });

  useEffect(() => {
    const selectedRows = table.getSelectedRowModel().rows;
    if (selectedRows.length > 0) {
      setSelectedUser(selectedRows[0].original);
      onUserSelect(selectedRows[0].original);
    } else {
      setSelectedUser(null);
      onUserSelect(null);
    }
  }, [rowSelection, setSelectedUser, table]);

  if (fetchCompanyLoading || fetchListLoading || isFetching) {
    return <CenterLoadingContainer />;
  }

  if (fetchCompanyError) return <div>Couldn't find company data</div>;
  if (fetchListError) return <div>Couldn't find users list</div>;

  return (
    <Container>
      <Row className="mt-4">
        <Col>
          <div className="mb-2">
            <TableFilter
              filterValue={globalFilter}
              setFilter={setGlobalFilter}
            />
          </div>

          <Table bordered hover variant="info" className="fixed-table">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} className={header.id == "emailAddress" ? "email-col" : ""}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => row.toggleSelected()}
                  style={{
                    backgroundColor: row.getIsSelected()
                      ? "lightblue"
                      : "white",
                    cursor: "pointer",
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{ backgroundColor: "inherit", cursor: "pointer" }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
};

export default UsersList;
